import { ParamMap, Params } from '@angular/router';
import moment from 'moment';
import { IBase, IPositionsDayRange } from 'src/app/models';
import { APP } from 'src/app/config/app';
import { ExpeditionStatus, ExpeditionType, ServiceType } from 'src/app/models/enums';
import {
  hasAnyFilterKey,
  parseEnumList,
  parseDate,
  parseId,
  parseIdList,
  parseStoredDate,
  serializeIdList,
  serializeStringList,
} from './filterUtils';

export interface ITripsFilters {
  selectedClients: IBase[];
  selectedOperators: IBase[];
  selectedTowns: IBase[];
  selectedServices: IBase[];
  selectedRoutes: IBase[];
  selectedDrivers: IBase[];
  selectedVehicles: IBase[];
  selectedTypes: ExpeditionType[];
  selectedServiceTypes: ServiceType[];
  selectedStatus: ExpeditionStatus[];
  from?: Date;
  to?: Date;
  selectedAvailabilityId: number | null;
}

export const TRIP_FILTER_KEYS = [
  'clients', 'operators', 'towns', 'services', 'routes', 'drivers', 'vehicles', 'types', 'serviceTypes', 'status',
  'availability', 'from', 'to'
] as const;

export const DEFAULT_TRIP_STATUS: ExpeditionStatus[] = [
  ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING
];

export function emptyTripsFilters(): ITripsFilters {
  return {
    selectedClients: [],
    selectedOperators: [],
    selectedTowns: [],
    selectedServices: [],
    selectedRoutes: [],
    selectedDrivers: [],
    selectedVehicles: [],
    selectedTypes: [],
    selectedServiceTypes: [],
    selectedStatus: DEFAULT_TRIP_STATUS,
    selectedAvailabilityId: null,
  };
}

export function serializeTripsFilters(f: ITripsFilters): Params {
  return {
    clients: serializeIdList(f.selectedClients),
    operators: serializeIdList(f.selectedOperators),
    towns: serializeIdList(f.selectedTowns),
    services: serializeIdList(f.selectedServices),
    routes: serializeIdList(f.selectedRoutes),
    drivers: serializeIdList(f.selectedDrivers),
    vehicles: serializeIdList(f.selectedVehicles),
    types: serializeStringList(f.selectedTypes),
    serviceTypes: serializeStringList(f.selectedServiceTypes),
    status: serializeStringList(f.selectedStatus),
    availability: f.selectedAvailabilityId ?? null,
    from: f.from ? moment(f.from).format('YYYY-MM-DD') : null,
    to: f.to ? moment(f.to).format('YYYY-MM-DD') : null,
  };
}

export function deserializeTripsFilters(
  params: ParamMap,
  fallback?: ITripsFilters
): ITripsFilters {
  if (!hasAnyFilterKey(params, TRIP_FILTER_KEYS) && fallback) return fallback;

  return {
    selectedClients: parseIdList(params.get('clients'), fallback?.selectedClients),
    selectedOperators: parseIdList(params.get('operators'), fallback?.selectedOperators),
    selectedTowns: parseIdList(params.get('towns'), fallback?.selectedTowns),
    selectedServices: parseIdList(params.get('services'), fallback?.selectedServices),
    selectedRoutes: parseIdList(params.get('routes'), fallback?.selectedRoutes),
    selectedDrivers: parseIdList(params.get('drivers'), fallback?.selectedDrivers),
    selectedVehicles: parseIdList(params.get('vehicles'), fallback?.selectedVehicles),
    selectedTypes: parseEnumList<ExpeditionType>(params.get('types')),
    selectedServiceTypes: parseEnumList<ServiceType>(params.get('serviceTypes')),
    selectedStatus: parseEnumList<ExpeditionStatus>(params.get('status'),
      fallback?.selectedStatus ?? DEFAULT_TRIP_STATUS),
    selectedAvailabilityId: parseId(params.get('availability')),
    from: parseDate(params.get('from')),
    to: parseDate(params.get('to')),
  };
}

export function withStoredTripsFilterLabels(filters: ITripsFilters, stored: ITripsFilters | null): ITripsFilters {
  if (!stored) return filters;
  const withLabels = <T extends IBase>(selected: T[], known: T[]): T[] => {
    const knownById = new Map(known.map((item) => [item.id, item]));
    return selected.map((item) => knownById.get(item.id) ?? item);
  };
  return {
    ...filters,
    selectedServices: withLabels(filters.selectedServices, stored.selectedServices),
    selectedRoutes: withLabels(filters.selectedRoutes, stored.selectedRoutes),
    selectedDrivers: withLabels(filters.selectedDrivers, stored.selectedDrivers),
    selectedVehicles: withLabels(filters.selectedVehicles, stored.selectedVehicles),
  };
}

export function resolvePositionsDayRange(params: ParamMap): IPositionsDayRange {
  const today = moment().startOf('day');
  const from = parseDate(params.get('from'));
  const minDay = from && moment(from).isAfter(today) ? undefined : from;
  const to = parseDate(params.get('to'));
  const maxDay = to && moment(to).isBefore(today) ? to : today.toDate();
  const requestedDay = moment(parseDate(params.get(APP.POSITIONS_DAY_PARAM)) ?? today);
  const latestDay = moment.min(requestedDay, moment(maxDay));
  const day = minDay ? moment.max(latestDay, moment(minDay)) : latestDay;
  return { day: day.startOf('day').toDate(), minDay, maxDay };
}

export function parseTripsFiltersFromStorage(json: string | null | undefined): ITripsFilters | null {
  if (!json) return null;
  try {
    const f = JSON.parse(json);
    const nn = <T>(arr: T[] | null | undefined): T[] => (arr ?? []).filter((x) => x != null);
    return {
      selectedClients: nn(f.selectedClients),
      selectedOperators: nn(f.selectedOperators),
      selectedTowns: nn(f.selectedTowns),
      selectedServices: nn(f.selectedServices),
      selectedRoutes: nn(f.selectedRoutes),
      selectedDrivers: nn(f.selectedDrivers),
      selectedVehicles: nn(f.selectedVehicles),
      selectedTypes: nn(f.selectedTypes),
      selectedServiceTypes: nn(f.selectedServiceTypes),
      selectedStatus: f.selectedStatus?.length ? f.selectedStatus : DEFAULT_TRIP_STATUS,
      selectedAvailabilityId: null,
      from: parseStoredDate(f.from),
      to: parseStoredDate(f.to),
    };
  } catch {
    return null;
  }
}
